import React, { useContext, useEffect } from 'react'
import { getJWTPayload, getRoleOrgList, loginOrgState, apContextState } from '@landscape/ui-lib'
import { GET_DI_FROM_ORGID, GET_AP_CONTEXT_ALLOWED_SCHOOLS } from '@landscape/gql'
import { get, isEmpty, isInteger } from 'lodash'
import { getClient } from 'apollo/configuration'
import { Login } from '.'
import { loginUser, logoutUser, selectUser, selectUserDiCode } from '@landscape/ui-redux'
import { SelectInstitution } from 'components/selectInstitution'
import { useDispatch, useSelector } from 'react-redux'
import { useMutation, useLazyQuery } from '@apollo/client'
import { useSetRecoilState, useRecoilState } from 'recoil'
import { ConfigContext } from 'AppProvider'

export const LoginManager: React.FC = ({ children }) => {
  const config = useContext(ConfigContext)
  const dispatch = useDispatch()
  const { isLoggedIn, isValidUser } = useSelector(selectUser)
  let di_code: string | null = useSelector(selectUserDiCode)
  const [loginOrg, setLoginOrg] = useRecoilState(loginOrgState)
  const [getDI] = useMutation(GET_DI_FROM_ORGID)
  const setAPContextState = useSetRecoilState(apContextState)

  const [getAPContextAllowedSchools, { data: allowedSchoolsData, loading, error }] = useLazyQuery(
    GET_AP_CONTEXT_ALLOWED_SCHOOLS,
    {
      fetchPolicy: 'cache-and-network',
    }
  )

  useEffect(() => {
    if (window.cb.core.iam.getAuthSession().isLoggedIn) {
      // When login handshake is already done, proceed to get JWT payload
      onCatapultLogin()
    }
  }, [])

  useEffect(() => {
    if (!loading && allowedSchoolsData) {
      const institutions = allowedSchoolsData.getAPContextAllowedSchools.institutions
      const isDiCodePresent = institutions.some((institution) => institution.di_code === di_code)
      setAPContextState(isDiCodePresent)
    }
  }, [allowedSchoolsData, loading])

  const onCatapultLogin = async () => {
    console.log(`Login handshake done at ${new Date().toLocaleString()}`)
    const jwtPayload = getJWTPayload()
    const loginTimestamp = get(jwtPayload, 'iat').toString()
    const username = get(jwtPayload, 'cb.un')

    const matchingRoleOrgList = getRoleOrgList()

    let rid, user_role, isValidUser, schoolName

    if (!matchingRoleOrgList || isEmpty(matchingRoleOrgList)) {
      isValidUser = false
    } else if (matchingRoleOrgList?.length === 1) {
      isValidUser = true
      user_role = matchingRoleOrgList[0].rn
      schoolName = matchingRoleOrgList[0].on
      rid = matchingRoleOrgList[0].rid
      getClient().orgId = matchingRoleOrgList[0].oid
      const { data: auditData } = await getDI({ variables: { loginTimestamp: loginTimestamp } })
      di_code = get(auditData, 'getDiCodeFromOrgId')
    } else {
      // Multiple orgs
      isValidUser = true
      if (isInteger(loginOrg.selectionIdx)) {
        user_role = loginOrg.roPairs[loginOrg.selectionIdx].rn
        schoolName = loginOrg.roPairs[loginOrg.selectionIdx].on
        rid = loginOrg.roPairs[loginOrg.selectionIdx].rid
        getClient().orgId = loginOrg.roPairs[loginOrg.selectionIdx].oid
        const { data: auditData } = await getDI({ variables: { loginTimestamp: loginTimestamp } })
        di_code = get(auditData, 'getDiCodeFromOrgId')
      } else {
        user_role = null
        schoolName = null
        rid = null
        di_code = null
        getClient().orgId = null
        setLoginOrg({
          selectionIdx: null,
          roPairs: [...matchingRoleOrgList],
        })
      }
    }

    // @TODO if invalid user, display error message. And gracefully log out the user
    const payload = { username, di_code, user_role, isValidUser, rid, schoolName }
    if (import.meta.env.VITE_NODE_ENV !== 'production') {
      console.log(JSON.stringify({ ...payload }))
    }
    dispatch(loginUser(payload))

    getAPContextAllowedSchools()
  }

  const onCatapultLogout = () => {
    dispatch(logoutUser())
    setLoginOrg({
      selectionIdx: null,
      roPairs: [],
    })
  }

  const cb = window.cb
  const iamBus = cb.core.iam.getEventBus()
  iamBus.on(cb.core.iam.events.AWSLogin, onCatapultLogin) // Listen to the event when user logs in
  iamBus.on(cb.core.iam.events.Logout, onCatapultLogout)

  if (!getJWTPayload() || config.disableLogin) {
    return <Login />
  }

  return (
    <>
      {isLoggedIn && !isValidUser && (
        <div className="container">
          <div>You are not authorized to access this site.</div>
        </div>
      )}

      {isLoggedIn && isValidUser && (!di_code || di_code.trim().length === 0) && (
        <SelectInstitution di_code={di_code || ''} />
      )}
      {isLoggedIn && isValidUser && di_code && di_code.trim().length > 0 && children}
    </>
  )
}
